<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AttachmentUpload } from '@/types/support'

const props = withDefaults(
  defineProps<{
    modelValue: File[]
    uploads?: AttachmentUpload[]
    maxSizeMb?: number
    acceptedTypes?: string
    maxFiles?: number
  }>(),
  {
    uploads: () => [],
    maxSizeMb: 10,
    acceptedTypes: 'image/png,image/jpeg,application/pdf',
    maxFiles: 10,
  },
)

const emit = defineEmits<{
  'update:modelValue': [files: File[]]
  add: [file: File]
  remove: [tempId: string]
}>()

const { t } = useI18n()

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const fileError = ref('')

const accepted = computed(() => props.acceptedTypes.split(',').map((s) => s.trim()))

function validateFile(file: File): string {
  if (!accepted.value.includes(file.type)) {
    return t('support.attachments.error_type')
  }
  if (file.size > props.maxSizeMb * 1024 * 1024) {
    return t('support.attachments.error_size')
  }
  return ''
}

function handleFiles(fileList: FileList | null): void {
  if (!fileList) return
  const files = Array.from(fileList)

  if (props.modelValue.length + files.length > props.maxFiles) {
    fileError.value = t('support.attachments.error_max')
    return
  }

  for (const file of files) {
    const err = validateFile(file)
    if (err) {
      fileError.value = err
      return
    }
  }

  fileError.value = ''
  for (const file of files) {
    emit('update:modelValue', [...props.modelValue, file])
    emit('add', file)
  }
}

function triggerBrowse(): void {
  fileInput.value?.click()
}

function handleInputChange(event: Event): void {
  const input = event.target as HTMLInputElement
  handleFiles(input.files)
  if (fileInput.value) fileInput.value.value = ''
}

function handleDragEnter(event: DragEvent): void {
  event.preventDefault()
  isDragging.value = true
}

function handleDragOver(event: DragEvent): void {
  event.preventDefault()
}

function handleDragLeave(): void {
  isDragging.value = false
}

function handleDrop(event: DragEvent): void {
  event.preventDefault()
  isDragging.value = false
  handleFiles(event.dataTransfer?.files ?? null)
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
</script>

<template>
  <div>
    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      :accept="acceptedTypes"
      multiple
      class="hidden"
      @change="handleInputChange"
    />

    <!-- Drop zone -->
    <div
      class="border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer"
      :class="isDragging
        ? 'border-emerald-500 bg-emerald-500/5'
        : 'border-slate-600 hover:border-emerald-500/50 bg-slate-800/50'"
      role="button"
      tabindex="0"
      :aria-label="t('support.attachments.hint')"
      @click="triggerBrowse"
      @keydown.enter.prevent="triggerBrowse"
      @keydown.space.prevent="triggerBrowse"
      @dragenter="handleDragEnter"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <svg
        class="w-8 h-8 mx-auto mb-2 transition-colors"
        :class="isDragging ? 'text-emerald-400' : 'text-slate-500'"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      <p class="text-sm font-medium" :class="isDragging ? 'text-emerald-400' : 'text-slate-300'">
        {{ t('support.attachments.hint') }}
      </p>
      <p class="text-xs text-slate-500 mt-0.5">{{ t('support.attachments.types_hint') }}</p>
    </div>

    <!-- Error message -->
    <p v-if="fileError" class="mt-1.5 text-xs text-red-400">{{ fileError }}</p>

    <!-- File chips -->
    <div v-if="uploads && uploads.length > 0" class="mt-3 flex flex-col gap-2">
      <div
        v-for="upload in uploads"
        :key="upload.tempId"
        class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm"
        :class="upload.status === 'error'
          ? 'bg-red-500/10 border border-red-500/30'
          : 'bg-slate-800 border border-slate-700'"
      >
        <!-- File icon -->
        <svg class="w-4 h-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>

        <!-- Filename + size -->
        <div class="flex-1 min-w-0">
          <span class="text-slate-300 truncate block">{{ upload.file.name }}</span>
          <span class="text-xs text-slate-500">{{ formatBytes(upload.file.size) }}</span>
        </div>

        <!-- Status indicator -->
        <div class="shrink-0 flex items-center gap-2">
          <!-- Spinner while uploading -->
          <svg
            v-if="upload.status === 'uploading'"
            class="animate-spin w-4 h-4 text-emerald-400"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>

          <!-- Success check -->
          <svg
            v-else-if="upload.status === 'done'"
            class="w-4 h-4 text-emerald-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>

          <!-- Error retry -->
          <span
            v-else-if="upload.status === 'error'"
            class="text-xs text-red-400"
          >
            {{ t('support.attachments.upload_failed') }}
          </span>

          <!-- Remove button -->
          <button
            type="button"
            class="w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-white hover:bg-slate-700/50 transition-colors"
            :aria-label="t('support.attachments.remove')"
            @click.stop="emit('remove', upload.tempId)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
